/**
 * Frontend Error Reporter for Rails Error Dashboard
 *
 * Captures and reports frontend errors to the backend for centralized monitoring.
 */

interface ErrorContext {
  component?: string;
  [key: string]: unknown;
}

interface ErrorPayload {
  error_type: string;
  message: string;
  backtrace: string[];
  url: string;
  component?: string;
  context?: ErrorContext;
}

// Rate limiting: track recent errors to prevent flooding
const recentErrors = new Map<string, number>();
const RATE_LIMIT_WINDOW_MS = 60_000; // 1 minute
const MAX_ERRORS_PER_WINDOW = 10;

const getCSRFToken = (): string | null => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") ?? null;
};

const getErrorFingerprint = (errorType: string, message: string): string => {
  return `${errorType}:${message}`;
};

const isRateLimited = (fingerprint: string): boolean => {
  const now = Date.now();

  // Clean up old entries
  for (const [key, timestamp] of recentErrors) {
    if (now - timestamp > RATE_LIMIT_WINDOW_MS) {
      recentErrors.delete(key);
    }
  }

  // Check if we've exceeded the limit
  if (recentErrors.size >= MAX_ERRORS_PER_WINDOW) {
    return true;
  }

  // Check if this specific error was recently reported
  if (recentErrors.has(fingerprint)) {
    return true;
  }

  recentErrors.set(fingerprint, now);
  return false;
};

const sendErrorToBackend = async (payload: ErrorPayload): Promise<void> => {
  const csrfToken = getCSRFToken();

  if (!csrfToken) {
    console.warn("[ErrorReporter] CSRF token not found, skipping error report");
    return;
  }

  try {
    await fetch("/api/errors", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify(payload),
    });
  } catch (fetchError) {
    // Silently fail - we don't want error reporting to cause more errors
    console.warn("[ErrorReporter] Failed to report error:", fetchError);
  }
};

/**
 * Report an error to the Rails Error Dashboard
 *
 * @param error - The error to report (Error object or any value)
 * @param context - Additional context about where the error occurred
 *
 * @example
 * try {
 *   riskyOperation();
 * } catch (error) {
 *   reportError(error, { component: 'MyComponent' });
 * }
 */
export const reportError = (error: unknown, context: ErrorContext = {}): void => {
  const errorObj = error instanceof Error ? error : new Error(String(error));
  const errorType = errorObj.name || "Error";
  const message = errorObj.message || "Unknown error";
  const fingerprint = getErrorFingerprint(errorType, message);

  if (isRateLimited(fingerprint)) {
    return;
  }

  const payload: ErrorPayload = {
    error_type: errorType,
    message,
    backtrace: errorObj.stack?.split("\n").map((line) => line.trim()) ?? [],
    url: window.location.href,
    component: context.component,
    context,
  };

  sendErrorToBackend(payload);
};

/**
 * Set up global error handlers to automatically capture uncaught errors
 *
 * Call this once during app initialization.
 */
export const setupGlobalErrorHandlers = (): void => {
  // Handle uncaught errors
  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, {
      component: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    reportError(error, {
      component: "unhandledrejection",
    });
  });
};
